import {
  MouseEventHandler,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react"
import { X } from "lucide-react"
import { useTranslations } from "modules/i18n/hooks/useTranslations"

// Note: This is a tsx translation with a few small tweaks for its new environment of:
// rails/app/assets/javascripts/components/promos/DonationDialog/index.jsx
// Please make sure to update both at the same time for future changes
// TODO: Create a component library so can more easily share things between frontend/rails

interface PersonProps {
  avatar: string
  name: string
  caption: string
}

interface DonationDialogProps {
  title: string
  body: string
  donateText: string
  person: PersonProps
  onClose: Function
  onClick: MouseEventHandler<HTMLButtonElement>
  creativeRef: RefObject<HTMLDivElement>
  modal?: boolean
}

export const DonationDialog = ({
  title,
  body,
  donateText,
  person,
  onClose,
  onClick,
  creativeRef,
  modal = true,
}: DonationDialogProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const t = useTranslations("promos")
  // Purposefully uses showModal not open=true to trigger it as per docs even though it's a bit weird
  // See: https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/dialog#open
  // Modal vs normal = promo previews vs actual

  useLayoutEffect(() => {
    if (dialogRef.current?.open) {
      return
    }
    modal ? dialogRef.current?.showModal?.() : dialogRef.current?.show?.()
  }, [modal, dialogRef])

  // Calls onClose handler as part of the eventListener,
  // specifically don't include it here so we don't double trigger our event tracking
  const handleClose = () => {
    dialogRef?.current?.open && dialogRef?.current?.close()
  }

  const onCloseWrapper = useCallback(() => onClose(), [onClose])

  const handleClickOutside = useCallback(
    (clickEvent: PointerEvent) => {
      if (clickEvent.target === dialogRef.current) {
        handleClose()
      }
    },
    [dialogRef],
  )

  useEffect(() => {
    const currentDialogRef = dialogRef?.current
    currentDialogRef?.addEventListener("close", onCloseWrapper)

    // Safari doesn't support clicking outside dialogs to close them (via closedBy option),
    // this can be removed once it does (and people given time to update as per usual)
    const requireManualClickOutsideSupport = !(`closedBy` in HTMLDialogElement.prototype)
    if (requireManualClickOutsideSupport) {
      currentDialogRef?.addEventListener("click", handleClickOutside)
    }
    return () => {
      currentDialogRef?.removeEventListener("close", onCloseWrapper)

      if (requireManualClickOutsideSupport) {
        currentDialogRef?.removeEventListener("click", handleClickOutside)
      }
    }
  }, [dialogRef, onCloseWrapper, handleClickOutside])

  // This is here because eslint doesn't recognise closedby it has been widely supported since 2022 (except safari)
  // closedby "any" means click backdrop, escape key, or x will close it.
  // Default for browsers that don't recognise it should be escape or X
  // None = dev only controlled, ie X click, useful for previews of promo
  const closedByType = { closedby: modal ? "any" : "none" } as const
  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="dialog-header"
      className="m-auto overflow-visible rounded-md border-none p-0 backdrop:fixed backdrop:inset-0 backdrop:h-full backdrop:bg-black/50 sm:items-start"
      {...closedByType}
    >
      <section
        ref={creativeRef}
        className="rounded-md bg-red-500 p-6 text-white shadow-lg sm:max-w-lg"
      >
        <button
          className="absolute top-4 right-4 flex cursor-pointer items-center justify-center rounded-full border-none bg-transparent p-3 text-white hover:bg-black/10"
          type="button"
          onClick={handleClose}
          aria-label={t("button.close")}
        >
          <X stroke="currentColor" className="size-6" />
        </button>

        <div className="-mt-14 flex justify-center">
          <img
            className="size-16 rounded-full border border-solid border-gray-200"
            src={person.avatar}
            alt=""
          />
        </div>

        <div className="mt-4 text-center font-sans">
          <h2
            id="dialog-header"
            className="font-sans-heading mt-9 text-xl font-semibold break-words"
          >
            {title}
          </h2>
          <p className="mt-5 text-base leading-snug break-words">{body}</p>
          <hr className="mx-auto my-4 max-w-5 border-white" />
          <div className="flex items-center justify-center">
            <div className="text-center">
              <div className="text-base">{person.name}</div>
              <div className="text-xs">{person.caption}</div>
            </div>
          </div>
        </div>

        <div className="my-6 flex justify-center">
          <button
            id="goToDonationsButton"
            className="cursor-pointer rounded-sm border-none bg-white px-12 py-3 text-base font-semibold break-words text-gray-900 transition-colors duration-300 hover:bg-[#f1f1f2]"
            type="button"
            onClick={onClick}
            data-testid="actionButton"
          >
            {donateText}
          </button>
        </div>
      </section>
    </dialog>
  )
}
